"use client";

import { useMemo } from "react";
import {
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { FunctionGraph } from "@/components/tools/calculus/function-graph";
import type {
  InterpolationView,
  LINEAR_INTERPOLATION_CALCULATOR_STRINGS,
} from "@/components/tools/linear-interpolation-calculator";
import type { PlaneScales } from "@/components/tools/math/coordinate-plane";
import type { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  bilinear,
  fraction,
  interpolateTable,
  inverseLerp,
  inverseTable,
  lerp,
} from "@/lib/tool-kit/calculus/interpolation";
import type { Window } from "@/lib/tool-kit/calculus/plot";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Strings = typeof LINEAR_INTERPOLATION_CALCULATOR_STRINGS;
type Fmt = ReturnType<typeof useToolNumber>;
type TwoView = Extract<InterpolationView, { kind: "two" }>;
type TableView = Extract<InterpolationView, { kind: "table" }>;
type GridView = Extract<InterpolationView, { kind: "bilinear" }>;

function d(fmt: Fmt, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return value === 0 ? fmt(0) : fmt(value, { maximumSignificantDigits: 10 });
}

function windowFor(xs: number[], ys: number[]): Window {
  const xPad = (Math.max(...xs) - Math.min(...xs)) * 0.2 || 1;
  const yPad = (Math.max(...ys) - Math.min(...ys)) * 0.2 || 1;
  return {
    xMin: Math.min(...xs) - xPad,
    xMax: Math.max(...xs) + xPad,
    yMin: Math.min(...ys) - yPad,
    yMax: Math.max(...ys) + yPad,
  };
}

function Polyline({
  points,
  scales,
}: {
  points: { x: number; y: number }[];
  scales: PlaneScales;
}) {
  const path = points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"}${scales.x(p.x).toFixed(1)} ${scales.y(p.y).toFixed(1)}`
    )
    .join(" ");
  return (
    <>
      <path d={path} fill="none" stroke={CHART_COLORS[0]} strokeWidth={2.2} />
      {points.map((p) => (
        <circle
          cx={scales.x(p.x)}
          cy={scales.y(p.y)}
          fill={CHART_COLORS[0]}
          key={`${p.x}-${p.y}`}
          r={3.5}
        />
      ))}
    </>
  );
}

function label(strings: Strings, fmt: Fmt, x: number, y: number): string {
  return t(strings.pointLabelText, { x: d(fmt, x), y: d(fmt, y) });
}

function TwoPoints({
  view,
  strings,
  fmt,
}: {
  view: TwoView;
  strings: Strings;
  fmt: Fmt;
}) {
  const { x1, y1, x2, y2, x } = view;
  const y = lerp(x1, y1, x2, y2, x);
  const slope = (y2 - y1) / (x2 - x1);
  const position = fraction(x1, x2, x);
  const inverse = view.y === null ? null : inverseLerp(x1, y1, x2, y2, view.y);
  const xs = [
    x1,
    x2,
    x,
    ...(inverse !== null && Number.isFinite(inverse) ? [inverse] : []),
  ];
  const ys = [y1, y2, y, ...(view.y === null ? [] : [view.y])];
  const window = windowFor(xs, ys);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.yResult, { x: d(fmt, x) })}
          value={d(fmt, y)}
        />
        {view.y !== null ? (
          <StatCard
            label={t(strings.xResult, { y: d(fmt, view.y) })}
            value={
              inverse === null || Number.isNaN(inverse) ? "—" : d(fmt, inverse)
            }
          />
        ) : null}
        <StatCard label={strings.slopeResult} value={d(fmt, slope)} />
        <StatCard
          label={strings.positionResult}
          value={fmt(position, { style: "percent", maximumFractionDigits: 2 })}
        />
      </StatGrid>
      {position < 0 || position > 1 ? (
        <Notice>{strings.extrapolatedPoints}</Notice>
      ) : null}
      <div className="space-y-1">
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className="text-muted-foreground text-sm">{strings.formulaY}</p>
        <p className="font-mono text-foreground text-sm">
          {t(strings.workingY, {
            y1: d(fmt, y1),
            x: d(fmt, x),
            x1: d(fmt, x1),
            y2: d(fmt, y2),
            x2: d(fmt, x2),
            result: d(fmt, y),
          })}
        </p>
        {view.y !== null && inverse !== null && Number.isNaN(inverse) ? (
          <Notice>{strings.inverseFlat}</Notice>
        ) : null}
        {view.y !== null && inverse !== null && !Number.isNaN(inverse) ? (
          <>
            <p className="text-muted-foreground text-sm">{strings.formulaX}</p>
            <p className="font-mono text-foreground text-sm">
              {t(strings.workingX, {
                x1: d(fmt, x1),
                y: d(fmt, view.y),
                y1: d(fmt, y1),
                x2: d(fmt, x2),
                y2: d(fmt, y2),
                result: d(fmt, inverse),
              })}
            </p>
          </>
        ) : null}
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
        <FunctionGraph
          downloadPngLabel={strings.downloadPng}
          downloadSvgLabel={strings.downloadSvg}
          filename="linear-interpolation-graph"
          points={[
            {
              x: x1,
              y: y1,
              color: CHART_COLORS[0],
              label: label(strings, fmt, x1, y1),
              below: true,
            },
            {
              x: x2,
              y: y2,
              color: CHART_COLORS[0],
              label: label(strings, fmt, x2, y2),
              below: true,
            },
            { x, y, color: CHART_COLORS[3], label: label(strings, fmt, x, y) },
            ...(view.y !== null && inverse !== null && Number.isFinite(inverse)
              ? [
                  {
                    x: inverse,
                    y: view.y,
                    color: CHART_COLORS[2],
                    label: label(strings, fmt, inverse, view.y),
                  },
                ]
              : []),
          ]}
          series={[
            {
              f: (v) => lerp(x1, y1, x2, y2, v),
              color: CHART_COLORS[0],
              label: strings.seriesLine,
            },
          ]}
          title={strings.graphTitleTwo}
          window={window}
        />
      </div>
    </>
  );
}

function Table({
  view,
  strings,
  fmt,
}: {
  view: TableView;
  strings: Strings;
  fmt: Fmt;
}) {
  const { points, x } = view;
  const result = interpolateTable(points, x);
  const inverse = useMemo(
    () => (view.y === null ? null : inverseTable(points, view.y)),
    [points, view.y]
  );
  const window = useMemo(() => {
    const xs = [...points.map((p) => p.x), x];
    const ys = [...points.map((p) => p.y), ...(result ? [result.value] : [])];
    return windowFor(xs, ys);
  }, [points, x, result]);
  if (result === null) {
    return <ErrorNotice>{strings.tableNeedsTwo}</ErrorNotice>;
  }
  const [p, q] = result.segment;
  let warning: string | null = null;
  if (result.extrapolated === "below") {
    warning = strings.extrapolatedBelow;
  } else if (result.extrapolated === "above") {
    warning = strings.extrapolatedAbove;
  } else if (result.exact) {
    warning = strings.exactMatch;
  }
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.yResult, { x: d(fmt, x) })}
          value={d(fmt, result.value)}
        />
        <StatCard
          label={strings.segmentResult}
          value={t(strings.segmentText, {
            x1: d(fmt, p.x),
            y1: d(fmt, p.y),
            x2: d(fmt, q.x),
            y2: d(fmt, q.y),
          })}
        />
        <StatCard
          label={strings.slopeResult}
          value={d(fmt, (q.y - p.y) / (q.x - p.x))}
        />
      </StatGrid>
      {warning ? <Notice>{warning}</Notice> : null}
      <div className="space-y-1">
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className="text-muted-foreground text-sm">{strings.formulaY}</p>
        <p className="font-mono text-foreground text-sm">
          {t(strings.workingY, {
            y1: d(fmt, p.y),
            x: d(fmt, x),
            x1: d(fmt, p.x),
            y2: d(fmt, q.y),
            x2: d(fmt, q.x),
            result: d(fmt, result.value),
          })}
        </p>
      </div>
      {view.y !== null && inverse ? (
        <div className="space-y-1">
          <h3 className={WIDGET_H3}>
            {t(strings.inverseSolutions, { y: d(fmt, view.y) })}
          </h3>
          <p className="font-mono text-foreground text-sm">
            {inverse.length > 0
              ? inverse.map((v) => d(fmt, v)).join(", ")
              : t(strings.inverseNone, { y: d(fmt, view.y) })}
          </p>
        </div>
      ) : null}
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
        <FunctionGraph
          downloadPngLabel={strings.downloadPng}
          downloadSvgLabel={strings.downloadSvg}
          filename="table-interpolation-graph"
          over={(scales) => <Polyline points={points} scales={scales} />}
          points={[
            {
              x,
              y: result.value,
              color: CHART_COLORS[3],
              label: label(strings, fmt, x, result.value),
            },
          ]}
          series={[
            {
              f: () => Number.NaN,
              color: CHART_COLORS[0],
              label: strings.seriesData,
            },
          ]}
          title={strings.graphTitleTable}
          window={window}
        />
      </div>
    </>
  );
}

function Grid({
  view,
  strings,
  fmt,
}: {
  view: GridView;
  strings: Strings;
  fmt: Fmt;
}) {
  const { grid, x, y } = view;
  const r = bilinear(grid, x, y);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.valueResult}
          value={d(fmt, r.value)}
        />
        <StatCard
          label={strings.positionX}
          value={fmt(r.tx, { style: "percent", maximumFractionDigits: 2 })}
        />
        <StatCard
          label={strings.positionY}
          value={fmt(r.ty, { style: "percent", maximumFractionDigits: 2 })}
        />
      </StatGrid>
      {r.extrapolated ? <Notice>{strings.extrapolatedGrid}</Notice> : null}
      <div className="space-y-1">
        <h3 className={WIDGET_H3}>{strings.gridHeading}</h3>
        <p className="font-mono text-foreground text-sm">
          {t(strings.gridStep1, {
            y1: d(fmt, grid.y1),
            q11: d(fmt, grid.q11),
            tx: d(fmt, r.tx),
            q21: d(fmt, grid.q21),
            r1: d(fmt, r.atY1),
          })}
        </p>
        <p className="font-mono text-foreground text-sm">
          {t(strings.gridStep2, {
            y2: d(fmt, grid.y2),
            q12: d(fmt, grid.q12),
            tx: d(fmt, r.tx),
            q22: d(fmt, grid.q22),
            r2: d(fmt, r.atY2),
          })}
        </p>
        <p className="font-mono text-foreground text-sm">
          {t(strings.gridStep3, { ty: d(fmt, r.ty), result: d(fmt, r.value) })}
        </p>
      </div>
    </>
  );
}

export function InterpolationResults({
  view,
  strings,
  fmt,
}: {
  view: Exclude<InterpolationView, { kind: "problem" }>;
  strings: Strings;
  fmt: Fmt;
}) {
  if (view.kind === "two") {
    return <TwoPoints fmt={fmt} strings={strings} view={view} />;
  }
  return view.kind === "table" ? (
    <Table fmt={fmt} strings={strings} view={view} />
  ) : (
    <Grid fmt={fmt} strings={strings} view={view} />
  );
}
